import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { UseSetLenguajePorPais } from "../hooks/UseSetLenguajePorPais";
import { useAuthContext } from "../hooks/Auth";
import { get_asignaturas_organizacion } from "../services/api";

type FormattedAsignaturaOrganizacion = {
  id: string;
  nombre: string;
  id_asignatura: string;
  id_nivel_asignatura: string;
};

type SelectedAsignaturaProp =
  | string
  | FormattedAsignaturaOrganizacion
  | null
  | undefined;

type SelectedNivelProp =
  | string
  | { id?: string; nombre?: string }
  | null
  | undefined;

interface Props {
  nivelId: SelectedNivelProp;
  onAsignaturaChange: (
    asignatura: FormattedAsignaturaOrganizacion
  ) => void;
  selectedAsignatura: SelectedAsignaturaProp;
}

const EMPTY_ASIGNATURA: FormattedAsignaturaOrganizacion = {
  id: "",
  nombre: "",
  id_asignatura: "",
  id_nivel_asignatura: "",
};

function normalizeId(value: SelectedNivelProp | SelectedAsignaturaProp): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "id" in value) {
    return String((value as any).id ?? "");
  }
  return "";
}

const GetAsignaturasOrganizacion: FC<Props> = ({
  nivelId,
  onAsignaturaChange,
  selectedAsignatura,
}) => {
  const [data, setData] = useState<FormattedAsignaturaOrganizacion[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  const { user } = useAuthContext();
  const { t } = useTranslation();

  UseSetLenguajePorPais();

  const IS_FROM_URUGUAY = user?.pais === "UY";
  const nivelIdStr = useMemo(() => normalizeId(nivelId), [nivelId]);

  useEffect(() => {
    setSelectedId(normalizeId(selectedAsignatura));
  }, [selectedAsignatura]);

  useEffect(() => {
  let isMounted = true;

  async function fetchAsignaturasPorNivel() {
    if (!nivelIdStr) {
      if (isMounted) {
        setData([]);
        setSelectedId("");
      }
      return;
    }

    try {
      const { data: responseData, status } =
        await get_asignaturas_organizacion(nivelIdStr);

      if (status !== 200) {
        console.error("GetAsignaturasOrganizacion: status no exitoso", {
          status,
          responseData,
        });

        if (isMounted) {
          setData([]);
          setSelectedId("");
        }
        return;
      }

      const raw = Array.isArray(responseData?.body)
        ? responseData.body
        : Array.isArray(responseData)
        ? responseData
        : [];

      const asignaturasArray: FormattedAsignaturaOrganizacion[] = raw
        .map((row: any) => {
          const idAsignatura = String(row?.id_asignatura ?? "");
          const idNivelAsignatura = String(row?.id_nivel_asignatura ?? "");
          const nombreAsignatura = String(row?.asignatura ?? "");

          return {
            id: idAsignatura,
            nombre: nombreAsignatura,
            id_asignatura: idAsignatura,
            id_nivel_asignatura: idNivelAsignatura,
          };
        })
        .filter((x: any) => x.id && x.nombre);

      if (!isMounted) return;

      setData(asignaturasArray);
    } catch (error) {
      console.error("GetAsignaturasOrganizacion: error llamando API", error);
      if (isMounted) {
        setData([]);
        setSelectedId("");
      }
    }
  }

  fetchAsignaturasPorNivel();

  return () => {
    isMounted = false;
  };
}, [nivelIdStr]);

useEffect(() => {
  const currentId = normalizeId(selectedAsignatura);

  if (!currentId) return;
  if (!data.length) return;

  const exists = data.some((a) => a.id === currentId);

  if (!exists) {
    setSelectedId("");
    onAsignaturaChange(EMPTY_ASIGNATURA);
  }
}, [data, selectedAsignatura, onAsignaturaChange]);

  const handleAsignaturaChange = (event: SelectChangeEvent<any>) => {
    const nextId = String(event.target.value ?? "");
    setSelectedId(nextId);

    const asignaturaSeleccionada =
      data.find((item) => item.id === nextId) ?? EMPTY_ASIGNATURA;

    onAsignaturaChange(asignaturaSeleccionada);
  };

  return (
    <Grid item xs={12}>
      <Box>
        <Typography
          sx={{
            fontFamily: "montserrat",
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#336B87",
          }}
        >
          {!IS_FROM_URUGUAY ? t("asignatura") : "Materia *"}
        </Typography>

        <FormControl fullWidth>
          <Select
            labelId="select-asignatura-organizacion"
            id="select-asignatura-organizacion"
            variant="outlined"
            value={selectedId}
            displayEmpty
            onChange={handleAsignaturaChange}
            disabled={!nivelIdStr}
            sx={{
              fontFamily: "montserrat",
              fontSize: "14px",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& fieldset": { borderColor: "#BDBDBD" },
              "&:hover fieldset": { borderColor: "#9E9E9E" },
              "&.Mui-focused fieldset": { borderColor: "#005559" },
              "& .MuiSelect-select": {
                color: selectedId ? "#005559" : "#B3D1D1",
              },
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            <option hidden value="">
              {!nivelIdStr
                ? "Selecciona primero un nivel"
                : "Selecciona por ejemplo: Matemáticas"}
            </option>

            {data.map((asignatura) => (
              <MenuItem
                key={`${asignatura.id_asignatura}-${asignatura.id_nivel_asignatura}`}
                value={asignatura.id}
                sx={{ color: "#005559" }}
              >
                <Typography
                  sx={{
                    fontFamily: "montserrat",
                    fontSize: "0.8rem",
                    color: "#336B87",
                  }}
                >
                  {asignatura.nombre}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Grid>
  );
};

export default GetAsignaturasOrganizacion;